/**
 * `sync.decks.deck(id).properties.*` — deck-level metadata (composed for
 * linked decks via `useComposedDeckMeta` / `getComposedDeckMeta`).
 *
 * A LINKED deck (one shared to a study group) keeps its shared half —
 * `displayName`, `sources`, `materials`, … — on the group's row, and every
 * surface reads it composed from there. So a write of a shared field has to
 * land on the group row too, or nobody (the owner included) ever sees it:
 * `set()` writes the personal row as always AND, when the viewer owns the
 * shared deck, forwards the shared-scope half of the partial to every group
 * it is linked into.
 * Members never forward — the server refuses a non-owner's content edit,
 * and the folder/reorder edits a member may make go through the group
 * facade directly.
 */

import { DECK_FIELD_SCOPES } from '@flashka/shared/sync-engine/schema/field-scopes';
import type { SGDeckProps } from '@flashka/shared/sync-engine/schema/group-entities';
import type { DeckId } from '@flashka/shared/sync-engine/schema/shared-types';

import {
  getComposedDeckMeta,
  useComposedDeckMeta,
} from '../internal/store/composed-selectors';
import { useStudyGroupStore } from '../internal/store/study-group-store';
import type { DeckProperties } from '../provider/sync-storage';
import { isActiveGroupMember } from '../study-groups/active-links';
import {
  currentUserId,
  getGroupMembershipsNow,
  updateDeckProperty,
} from '../study-groups/helpers';
import { store } from './helpers';
import type { ScopedProperties } from './types';

export function createPropertiesApi(deckId: DeckId): ScopedProperties {
  return {
    use: () => useComposedDeckMeta(deckId),
    get: () => getComposedDeckMeta(deckId) as DeckProperties | undefined,
    set: (partial) => {
      const current = store().deckProperties[deckId];
      // What the caller was looking at, read BEFORE the write lands.
      const composed = getComposedDeckMeta(deckId);
      store().setDeckProperty(deckId, { ...current, ...partial });
      forwardSharedFieldsToGroup(current, composed, partial);
    },
  };
}

function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Forward the shared-scope half of a partial to EVERY group row the viewer
 * publishes this deck into.
 *
 * One deck can be linked into several groups without forking (every link
 * owner-only), and each group holds its own row. A write that reached only
 * one of them left every other group on the old documents, notes and title
 * for good. So each link the viewer still belongs to and shared themselves
 * gets the patch, diffed against THAT group's row. A link to a group the
 * viewer has left is dormant (see `study-groups/active-links`) and is never
 * written: its socket is closed to them.
 *
 * A key is forwarded only when the caller actually CHANGED it — its value
 * differs from BOTH values the caller could have been looking at: the
 * personal row it was merged over, and the composed row every surface reads
 * (the FIRST live link's group row). Then it also has to differ from the
 * target group's row. Every check is load-bearing:
 *   - a caller that spreads a stale personal row (`{...raw, folder}`) must
 *     not push last month's name over a rename made on a group tile;
 *   - a caller that spreads the composed row (a language change that passes
 *     the name and colour it read) must not push group A's values over a
 *     rename made on group B's tile — equal to what it read is not a change,
 *     whatever group B's row says;
 *   - and a real change must not re-broadcast to a group already holding it.
 */
function forwardSharedFieldsToGroup(
  personal: DeckProperties | undefined,
  composed: DeckProperties | undefined,
  partial: Partial<DeckProperties>,
): void {
  const links = personal?.linkedGroups;
  if (!links?.length) return;
  const viewer = currentUserId();
  if (!viewer) return;
  const memberships = getGroupMembershipsNow();

  for (const link of links) {
    if (!isActiveGroupMember(link.groupId, memberships)) continue;
    const row = useStudyGroupStore.getState().groups[link.groupId]?.deckProps[
      link.sgDeckId
    ] as (SGDeckProps & Record<string, unknown>) | undefined;
    if (!row || row.addedByUserId !== viewer) continue;

    const patch: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(partial)) {
      if (DECK_FIELD_SCOPES[key as keyof typeof DECK_FIELD_SCOPES] !== 'shared') continue;
      if (sameValue(value, (personal as Record<string, unknown> | undefined)?.[key]))
        continue;
      if (sameValue(value, (composed as Record<string, unknown> | undefined)?.[key]))
        continue;
      if (sameValue(value, row[key])) continue;
      patch[key] = value;
    }
    if (Object.keys(patch).length === 0) continue;
    updateDeckProperty(link.groupId, link.sgDeckId, patch as Partial<SGDeckProps>);
  }
}
